import React from 'react';

import { createStyles, Loader, Paper, Table, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import usePackages from 'hooks/package/usePackages';
import { useNavigate } from 'react-router-dom';

const useStyles = createStyles(theme => ({
  container: {
    margin: `0 ${theme.spacing.md}px`,
  },
  table: {
    backgroundColor: theme.colors.gray[0],
  },
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  headerCell: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[7],
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    padding: theme.spacing.sm,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
  },
  cell: {
    padding: theme.spacing.sm,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    verticalAlign: 'middle',
  },
  packageName: {
    fontWeight: 500,
    fontSize: theme.fontSizes.sm,
  },
  chevron: {
    color: theme.colors.gray[5],
    width: 16,
    height: 16,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
    margin: `0 ${theme.spacing.md}px`,
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
    color: theme.colors.gray[6],
    margin: `0 ${theme.spacing.md}px`,
  },
}));

interface PackageListProps {
  search: string;
}

const PackageList = ({ search }: PackageListProps): JSX.Element => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const {
    data: packagesData,
    isLoading,
    isError,
  } = usePackages({
    search,
    isArchived: false,
    limit: '100', // Get more packages for mobile view
  });

  const handleRowClick = (packageId: string): void => {
    navigate(`/packages/${packageId}`);
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <Loader size='sm' />
      </div>
    );
  }

  if (isError || !packagesData?.pages) {
    return (
      <div className={classes.emptyState}>
        <Text>Error loading packages</Text>
      </div>
    );
  }

  // Flatten paginated data
  const allPackages = packagesData.pages.flatMap(page => page.data.results);

  if (allPackages.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Text>No packages found</Text>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Paper className={classes.table} radius={0}>
        <Table>
          <thead>
            <tr>
              <th className={classes.headerCell}>Name</th>
              <th className={classes.headerCell}>Active</th>
              <th className={classes.headerCell}>Billing Plans</th>
              <th className={classes.headerCell} style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {allPackages.map(pkg => (
              <tr key={pkg.packageId} className={classes.row} onClick={() => handleRowClick(pkg.packageId)}>
                <td className={classes.cell}>
                  <Text className={classes.packageName}>{pkg.name}</Text>
                </td>
                <td className={classes.cell}>
                  <Text size='sm'>{pkg.activeAthletePackageCount || 0}</Text>
                </td>
                <td className={classes.cell}>
                  <Text size='sm'>{pkg.billingPlans?.length || 0}</Text>
                </td>
                <td className={classes.cell}>
                  <IconChevronRight className={classes.chevron} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Paper>
    </div>
  );
};

export default PackageList;
